/**
 * UnderstandingReview — SONMI's shared moment of interpretation.
 *
 * Renders any `Understanding` regardless of kind. Domains create Understandings
 * server-side; this component displays them uniformly, waits for the person to
 * confirm, revise, or reject, and calls the shared confirm/reject endpoints.
 *
 * Props:
 *   understanding  — the Understanding object (from the backend)
 *   onResolved(u)  — called after confirm / reject with the resolved object
 *   onDismiss()    — optional: called when the person walks away
 *   testId         — optional dom test id root
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";

const CONCERN_ICON = {
  excluded:  "◦",
  anomaly:   "△",
  missing:   "◇",
  uncertain: "?",
};

const CONFIDENCE_TONE = {
  high:   "text-emerald-700",
  medium: "text-amber-700",
  low:    "text-rose-700",
};

export function UnderstandingReview({ understanding, onResolved, onDismiss, testId = "understanding" }) {
  const [expanded, setExpanded] = useState(
    understanding?.kind === "tippy_gratuity_import"
  );
  const [busy, setBusy] = useState(null);

  if (!understanding) return null;
  const { id, interpretation, state } = understanding;
  const done = state !== "pending";

  const confirm = async () => {
    setBusy("confirm");
    try {
      const r = await api.post(`/understanding/${id}/confirm`);
      toast.success("Understanding confirmed.");
      onResolved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't confirm.");
    } finally { setBusy(null); }
  };

  const reject = async () => {
    setBusy("reject");
    try {
      const r = await api.post(`/understanding/${id}/reject`, {});
      toast.message("Set aside. Nothing was applied.");
      onResolved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't set aside.");
    } finally { setBusy(null); }
  };

  return (
    <div className="bg-white subtle-border rounded-2xl p-6" data-testid={testId}>
      {/* Headline + scope */}
      <div className="flex items-start gap-3">
        <div className="w-1 self-stretch rounded-full bg-stone-900/70 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] label-eyebrow text-stone-400">SONMI understands this as</div>
          <div className="text-lg text-stone-900 mt-0.5" style={{ fontWeight: 500 }} data-testid={`${testId}-headline`}>
            {interpretation.headline}
          </div>
          <div className="text-xs text-stone-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            {interpretation.scope?.subject_label && (
              <span data-testid={`${testId}-subject`}>{interpretation.scope.subject_label}</span>
            )}
            {interpretation.scope?.date_range_label && (
              <span data-testid={`${testId}-range`}>· {interpretation.scope.date_range_label}</span>
            )}
            <span className={CONFIDENCE_TONE[interpretation.scope?.confidence || "high"]}>
              · confidence {interpretation.scope?.confidence || "high"}
            </span>
          </div>
          {interpretation.scope?.confidence_note && (
            <div className="text-[11px] text-stone-500 italic mt-1">{interpretation.scope.confidence_note}</div>
          )}
        </div>
        {done && (
          <div className="text-xs text-stone-400 shrink-0">
            {state === "confirmed" ? "confirmed" : state === "rejected" ? "set aside" : state}
          </div>
        )}
      </div>

      {/* Intent — SONMI's single sentence of intent */}
      <p className="text-sm text-stone-700 leading-relaxed mt-4" data-testid={`${testId}-intent`}>
        {interpretation.intent}
      </p>

      {/* Concerns — always visible if any */}
      {interpretation.concerns?.length > 0 && (
        <div className="mt-4 rounded-xl subtle-border bg-amber-50/40 p-3" data-testid={`${testId}-concerns`}>
          <div className="text-[10px] label-eyebrow text-amber-800 mb-2">Worth noting</div>
          <ul className="space-y-1">
            {interpretation.concerns.map((c, i) => (
              <li key={i} className="text-xs text-stone-700 flex items-start gap-2" data-testid={`${testId}-concern-${c.kind}-${i}`}>
                <span className="text-amber-700 mt-[1px]">{CONCERN_ICON[c.kind] || "•"}</span>
                <div className="flex-1">
                  <span className="text-stone-800">{c.label}</span>
                  {c.count != null && <span className="text-stone-500"> · {c.count}</span>}
                  {c.detail && <div className="text-stone-500 mt-0.5">{c.detail}</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Supported workflows with their pinned policy rules */}
      {interpretation.supported_workflows?.length > 0 && (
        <div className="mt-5" data-testid={`${testId}-policies`}>
          <div className="text-[10px] label-eyebrow text-stone-400 mb-2">Rules SONMI will apply</div>
          <div className="space-y-3">
            {interpretation.supported_workflows.map((w) => (
              <WorkflowPolicy key={w.key} workflow={w} testId={`${testId}-policy-${w.key}`} />
            ))}
          </div>
          <div className="mt-3 text-[11px] text-stone-500 italic">
            Does the policy above reflect current business rules? If not, choose "Something doesn't look right" — nothing will be applied.
          </div>
        </div>
      )}

      {/* Details — collapsed by default; opens on "Review details" */}
      {expanded && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {understanding.kind === "tippy_gratuity_import" && (
            <TippyMappingReview understanding={understanding} testId={testId} />
          )}
          {interpretation.records?.length > 0 && (
            <div data-testid={`${testId}-records`}>
              <div className="text-[10px] label-eyebrow text-stone-400 mb-2">Records collected</div>
              <ul className="space-y-1">
                {interpretation.records.map((r, i) => (
                  <li key={i} className="text-xs flex justify-between border-b border-stone-100 py-1">
                    <span className="text-stone-600">{r.label}</span>
                    <span className="text-stone-900" style={{ fontWeight: 500 }}>
                      {String(r.value)}
                      {r.hint && <span className="text-stone-400 font-normal"> · {r.hint}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {interpretation.supported_workflows?.length > 0 && (
            <div data-testid={`${testId}-workflows`}>
              <div className="text-[10px] label-eyebrow text-stone-400 mb-2">Workflows this evidence can drive</div>
              <ul className="space-y-1">
                {interpretation.supported_workflows.map((w) => (
                  <li key={w.key} className="text-xs border-b border-stone-100 py-1">
                    <div className="text-stone-900">{w.label}</div>
                    {w.will_produce && <div className="text-stone-500 mt-0.5">{w.will_produce}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* The three-way choice */}
      <div className="mt-5 pt-4 border-t border-stone-100">
        <div className="text-sm text-stone-800 mb-3">Does this match your understanding?</div>
        {done ? (
          <div className="text-xs text-stone-500 italic">
            {state === "confirmed"
              ? "You confirmed. Dependent calculations have been applied."
              : "You set this aside. Nothing was applied."}
            {onDismiss && (
              <button className="ml-3 text-stone-700 underline underline-offset-2" onClick={onDismiss} data-testid={`${testId}-dismiss`}>
                dismiss
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={confirm}
              disabled={!!busy}
              data-testid={`${testId}-confirm`}
              className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-50"
            >
              {busy === "confirm" ? "…" : "Yes, continue"}
            </button>
            <button
              onClick={() => setExpanded((v) => !v)}
              data-testid={`${testId}-review`}
              className="px-4 py-2 rounded-full subtle-border bg-white text-stone-700 text-sm hover:bg-stone-50 inline-flex items-center gap-1"
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Review details
            </button>
            <button
              onClick={reject}
              disabled={!!busy}
              data-testid={`${testId}-reject`}
              className="px-4 py-2 rounded-full text-stone-500 text-sm hover:text-stone-800 inline-flex items-center gap-1 disabled:opacity-50"
            >
              <AlertTriangle size={13} /> {busy === "reject" ? "…" : "Something doesn't look right"}
            </button>
          </div>
        )}
      </div>

      {/* Trust footer — two quiet sentences that answer trust questions before they're asked. */}
      <TrustFooter understanding={understanding} testId={testId} />
    </div>
  );
}

// ── Trust footer ───────────────────────────────────────────────────────────
// Two-line footer stating (a) which policy version was applied, (b) whether
// dependent records have been updated. Applies to ANY Understanding, not just
// payroll — wording generalizes via the workflow-noun helper below.
function TrustFooter({ understanding, testId }) {
  const state = understanding.state;
  const workflows = understanding.interpretation?.supported_workflows || [];
  // Show the highest policy version across the applicable workflows. If there
  // are none pinned, don't render the policy line — no dishonesty.
  const versions = workflows
    .map((w) => (w.rule_set ? w.rule_set.version : null))
    .filter((v) => v != null);
  const highestVersion = versions.length ? Math.max(...versions) : null;
  const workflowLabel = _workflowFamily(understanding);
  const workflowNoun = _workflowNoun(understanding);

  if (state === "pending") {
    return (
      <div className="mt-4 pt-3 border-t border-stone-100 text-[11px] text-stone-400 leading-relaxed" data-testid={`${testId}-trust-footer`}>
        {highestVersion != null && (
          <div>Prepared using {workflowLabel} Policy v{highestVersion}.</div>
        )}
        <div>No {workflowNoun} records have been updated yet.</div>
      </div>
    );
  }
  if (state === "confirmed") {
    const who = understanding.resolved_by || "you";
    const when = _formatWhen(understanding.confirmed_at);
    return (
      <div className="mt-4 pt-3 border-t border-stone-100 text-[11px] text-stone-500 leading-relaxed" data-testid={`${testId}-trust-footer`}>
        <div>Understanding confirmed by <span className="text-stone-800">{who}</span>{when && <> · <span className="text-stone-800">{when}</span></>}.</div>
        {highestVersion != null && (
          <div className="text-stone-400">Applied {workflowLabel} Policy v{highestVersion}.</div>
        )}
      </div>
    );
  }
  return null; // rejected / expired — no footer
}

function _workflowFamily(understanding) {
  // Attempts to infer a friendly family name from the Understanding.
  const domain = understanding.subject?.domain;
  if (domain === "payroll") return "Payroll";
  return (domain || "Workflow").slice(0, 1).toUpperCase() + (domain || "workflow").slice(1);
}

function _workflowNoun(understanding) {
  const domain = understanding.subject?.domain;
  if (domain === "payroll") return "payroll";
  return domain || "workflow";
}

function _formatWhen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default UnderstandingReview;


function TippyMappingReview({ understanding, testId }) {
  const action = (understanding.pending_actions || []).find(
    (item) => item.kind === "apply_tippy_import"
  );
  const summary = action?.payload?.summary;
  const entries = summary?.entries || [];

  if (!entries.length) {
    return (
      <div className="md:col-span-2 rounded-xl subtle-border bg-stone-50/60 p-3 text-xs text-stone-600">
        The Tippy report is preserved, but its Team mapping is not available in this older review record.
      </div>
    );
  }

  return (
    <div className="md:col-span-2" data-testid={`${testId}-tippy-mapping`}>
      <div className="flex flex-wrap items-end justify-between gap-2 mb-2">
        <div>
          <div className="text-[10px] label-eyebrow text-stone-400">Team mapping</div>
          <div className="text-xs text-stone-600 mt-1">
            Tippy card tips will be reported as CASHTIP Amount (Qualified) for tax purposes. Tippy has already paid these amounts.
          </div>
        </div>
        <div className="text-[11px] text-stone-500 tabular-nums">
          {summary.matched_count || 0} matched · {summary.unmatched_count || 0} unmatched · {summary.ambiguous_count || 0} ambiguous
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl subtle-border">
        <table className="w-full min-w-[760px] text-left text-xs">
          <thead className="bg-stone-50 text-stone-500">
            <tr>
              <th className="px-3 py-2 font-normal">Tippy Team member</th>
              <th className="px-3 py-2 font-normal">SONMI understands this as</th>
              <th className="px-3 py-2 font-normal">Mapping</th>
              <th className="px-3 py-2 font-normal">Payroll category</th>
              <th className="px-3 py-2 font-normal text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {entries.map((entry, index) => {
              const sourceName = [entry.tippy_first_name, entry.tippy_last_name].filter(Boolean).join(" ") || "Unnamed Team member";
              const mappedName = entry.matched_employee_name
                || (entry.candidates || []).map((candidate) => candidate.name).filter(Boolean).join(", ")
                || "Not mapped";
              const status = entry.match_status === "matched"
                ? "Matched"
                : entry.match_status === "ambiguous" ? "Needs review" : "Not mapped";
              return (
                <tr key={`${sourceName}-${index}`} className="align-top">
                  <td className="px-3 py-2 text-stone-900">{sourceName}</td>
                  <td className="px-3 py-2 text-stone-900">
                    {mappedName}
                    {entry.match_reason && <div className="text-[10px] text-stone-500 mt-0.5">{entry.match_reason}</div>}
                  </td>
                  <td className="px-3 py-2 text-stone-600">{status}</td>
                  <td className="px-3 py-2 text-stone-600">CASHTIP Amount (Qualified)</td>
                  <td className="px-3 py-2 text-stone-900 text-right tabular-nums">{formatMoney(entry.taxable_cash_gratuities)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t border-stone-200 bg-stone-50/70">
            <tr>
              <td colSpan="4" className="px-3 py-2 text-stone-600">
                Computed card-tip total
                {summary.reconciliation_delta != null && (
                  <span className="text-stone-400"> · reconciliation delta {formatMoney(summary.reconciliation_delta)}</span>
                )}
              </td>
              <td className="px-3 py-2 text-right text-stone-900 tabular-nums">{formatMoney(summary.computed_total_card)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString(undefined, { style: "currency", currency: "USD" });
}


// ── Workflow policy renderer ───────────────────────────────────────────────
const RULE_KIND_ICON = {
  attribution: "▸",
  eligibility: "◆",
  exclusion:   "×",
  rate:        "%",
  destination: "→",
};

const RULE_KIND_LABEL = {
  attribution: "Attribution",
  eligibility: "Eligibility",
  exclusion:   "Exclusion",
  rate:        "Rate",
  destination: "ADP destination",
};

function WorkflowPolicy({ workflow, testId }) {
  const [showReason, setShowReason] = useState(false);
  const rs = workflow.rule_set;
  return (
    <div className="rounded-xl subtle-border bg-stone-50/60 p-3" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm text-stone-900" style={{ fontWeight: 500 }}>{workflow.label}</div>
        {rs ? (
          <div className="text-[10px] text-stone-500 shrink-0" data-testid={`${testId}-version`}>
            policy v{rs.version} · effective {rs.effective_from}
          </div>
        ) : (
          <div className="text-[10px] text-amber-700 shrink-0">no pinned policy</div>
        )}
      </div>
      {workflow.will_produce && (
        <div className="text-[11px] text-stone-500 mt-0.5">{workflow.will_produce}</div>
      )}
      {rs?.rules?.length > 0 && (
        <ul className="mt-2 space-y-1">
          {rs.rules.map((r, i) => (
            <li key={i} className="text-[11px] text-stone-700 flex items-start gap-2" data-testid={`${testId}-rule-${r.kind}-${i}`}>
              <span className="text-stone-400 mt-[1px] w-16 shrink-0 text-[10px] uppercase tracking-wider">{RULE_KIND_LABEL[r.kind] || r.kind}</span>
              <div className="flex-1">
                <div className="text-stone-800">{r.label}</div>
                {showReason && r.reason && (
                  <div className="text-stone-500 italic mt-0.5">{r.reason}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {rs?.rules?.length > 0 && (
        <button
          onClick={() => setShowReason((v) => !v)}
          className="mt-2 text-[10px] text-stone-500 hover:text-stone-800 underline underline-offset-2"
          data-testid={`${testId}-toggle-reasons`}
        >
          {showReason ? "hide reasons" : "why these rules"}
        </button>
      )}
    </div>
  );
}
